import { api } from '../api';

/**
 * Sincronización Binary → portal de un legajo (VEN-540). Se dispara al abrir
 * la ficha en Búsqueda; Binary es la fuente y el portal se acomoda. Los tipos
 * espejan `server/src/modules/sync-binary/sync-binary.types.ts`.
 */

export type ResultadoSync =
  | 'sincronizado'
  | 'sin_cambios'
  | 'simulado'
  | 'binary_no_disponible'
  | 'titular_no_en_binary'
  | 'titular_alta_pendiente'
  | 'error';

export type AccionSync = 'corrio' | 'reciente' | 'en_curso' | 'no_aplica' | 'pausado';

export interface CambioCampo {
  asociadoId: string;
  nombre: string;
  campo: string;
  antes: string;
  despues: string;
}

export interface OmisionCampo {
  asociadoId: string;
  nombre: string;
  campo: string;
  motivo: string;
}

export interface AltaSync {
  asociadoId: string;
  afiId: string;
  nombre: string;
  parentesco: string | null;
}

export type TipoMarca =
  | 'vinculado'
  | 'baja_binary'
  | 'reactivado_binary'
  | 'no_en_binary'
  | 'vuelve_a_binary'
  | 'movido_a_otro_legajo'
  | 'traido_de_otro_legajo'
  | 'retitularizado';

export interface MarcaSync {
  asociadoId: string;
  nombre: string;
  tipo: TipoMarca;
  detalle?: string;
}

export interface Divergencia {
  tipo: string;
  asociadoId?: string;
  nombre?: string;
  afiId?: string;
  cabezaBinary?: string;
  legajoOtroId?: string;
  legajoOtroNumero?: string;
  campo?: string;
  valorBinary?: string;
  /** Qué habría que hacer, en texto. Vacío cuando no hay acción posible. */
  propuesta: string;
  detalle: string;
  /** La acción concreta que resuelve la divergencia, si hay una. */
  accion?: 'mover_a_otro_legajo' | 'traer_de_otro_legajo' | 'retitularizar';
  /** `true` si el sync la aplica solo; el retitularizado nunca lo es. */
  automatica?: boolean;
  /** Para `retitularizar`: el familiar que pasa a ser titular. */
  nuevoTitularAsociadoId?: string;
  /** La acción ya se aplicó en esta corrida. */
  aplicada?: boolean;
}

export interface ResultadoRetitularizar {
  legajoId: string;
  numero: string;
  titularAnterior: { asociadoId: string; nombre: string };
  titularNuevo: { asociadoId: string; nombre: string; afiId: string };
  /** Lo que queda atado al titular anterior y hay que mirar a mano. */
  avisos: string[];
}

/** La última corrida de un legajo (`legajo.binarySync`). */
export interface BinarySyncLegajo {
  at: string;
  por: string;
  origen: 'busqueda' | 'manual';
  resultado: ResultadoSync;
  simulado: boolean;
  afiIdTitular?: string;
  cabezaAfiId?: string;
  cambios: CambioCampo[];
  altas: AltaSync[];
  marcas: MarcaSync[];
  omitidos: OmisionCampo[];
  divergencias: Divergencia[];
  error?: string;
  duracionMs: number;
}

export interface RespuestaSync {
  accion: AccionSync;
  sync: BinarySyncLegajo | null;
}

export interface SyncBinaryEvento extends BinarySyncLegajo {
  id?: string;
  _id?: string;
  legajoId: string;
  numero: string;
}

/**
 * Si la última corrida es más nueva que esto, abrir la ficha no vuelve a
 * consultar Binary. El server aplica el mismo freno; acá se evita el POST.
 */
export const SYNC_BINARY_AUTO_CADA_MS = 10 * 60 * 1000;

export function syncEsReciente(at: string | undefined | null, ahora = Date.now()): boolean {
  if (!at) return false;
  const t = Date.parse(at);
  return !Number.isNaN(t) && ahora - t < SYNC_BINARY_AUTO_CADA_MS;
}

export async function sincronizarLegajoBinary(
  legajoId: string,
  forzar = false,
): Promise<RespuestaSync> {
  const { data } = await api.post<RespuestaSync>(`/sync-binary/legajo/${legajoId}`, { forzar });
  return data;
}

export async function historialSyncBinary(legajoId: string): Promise<SyncBinaryEvento[]> {
  const { data } = await api.get<SyncBinaryEvento[]>(`/sync-binary/legajo/${legajoId}/historial`);
  return data;
}

/**
 * Pasa la titularidad del legajo al familiar que Binary tiene como cabeza del
 * grupo. Solo ADMIN/SUPERADMIN; el server lo vuelve a confirmar contra Binary.
 */
export async function retitularizarLegajo(
  legajoId: string,
  nuevoTitularAsociadoId: string,
): Promise<ResultadoRetitularizar> {
  const { data } = await api.post<ResultadoRetitularizar>(
    `/sync-binary/legajo/${legajoId}/retitularizar`,
    { nuevoTitularAsociadoId },
  );
  return data;
}
